// ═══════════════════════════════════════════════════════════════
// MainStreet — Business Owner Portal Layout
// Separate layout for biz_owner role with filtered navigation
// Nav badges show pending/new item counts
// ═══════════════════════════════════════════════════════════════

import { useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Group, Button, Center, Loader, Badge } from '@mantine/core';
import {
  IconLayoutDashboard, IconHome, IconBuildingWarehouse,
  IconAddressBook, IconClipboardCheck, IconAlertTriangle,
  IconPackage, IconShieldCheck, IconMessage,
} from '@tabler/icons-react';
import { useMyOrg } from '../../../hooks/useMyOrg';
import { useMyStatusResponses } from '../../../hooks/useMyOrgData';
import { useMainStreetStore } from '../../../store/mainstreetStore';
import { useMainStreetOpsStore } from '../../../store/mainstreetOpsStore';
import { useAuthStore } from '../../../store/authStore';
import PortalDashboard from './PortalDashboard';
import PortalMyBusiness from './PortalMyBusiness';
import PortalFacilities from './PortalFacilities';
import PortalContacts from './PortalContacts';
import PortalStatusCheckIn from './PortalStatusCheckIn';
import PortalDamageReport from './PortalDamageReport';
import PortalResourceOffers from './PortalResourceOffers';
import PortalReentryPasses from './PortalReentryPasses';
import PortalFeed from './PortalFeed';
import PortalOnboarding from './PortalOnboarding';
import { brand } from '../../../lib/colors';

const PORTAL_NAV = [
  { label: 'Dashboard', path: '/mainstreet', icon: IconLayoutDashboard, exact: true, badgeKey: '' },
  { label: 'My Business', path: '/mainstreet/my-business', icon: IconHome, badgeKey: '' },
  { label: 'Facilities', path: '/mainstreet/facilities', icon: IconBuildingWarehouse, badgeKey: '' },
  { label: 'Contacts', path: '/mainstreet/contacts', icon: IconAddressBook, badgeKey: '' },
  { label: 'Status', path: '/mainstreet/status', icon: IconClipboardCheck, badgeKey: 'status' },
  { label: 'Damage', path: '/mainstreet/damage', icon: IconAlertTriangle, badgeKey: '' },
  { label: 'Resources', path: '/mainstreet/resources', icon: IconPackage, badgeKey: 'resources' },
  { label: 'Re-entry', path: '/mainstreet/reentry', icon: IconShieldCheck, badgeKey: 'reentry' },
  { label: 'Feed', path: '/mainstreet/feed', icon: IconMessage, badgeKey: 'feed' },
];

function resolvePortalPage(pathname: string) {
  if (pathname === '/mainstreet' || pathname === '/mainstreet/') return <PortalDashboard />;
  if (pathname.startsWith('/mainstreet/my-business')) return <PortalMyBusiness />;
  if (pathname.startsWith('/mainstreet/facilities')) return <PortalFacilities />;
  if (pathname.startsWith('/mainstreet/contacts')) return <PortalContacts />;
  if (pathname.startsWith('/mainstreet/status')) return <PortalStatusCheckIn />;
  if (pathname.startsWith('/mainstreet/damage')) return <PortalDamageReport />;
  if (pathname.startsWith('/mainstreet/resources')) return <PortalResourceOffers />;
  if (pathname.startsWith('/mainstreet/reentry')) return <PortalReentryPasses />;
  if (pathname.startsWith('/mainstreet/feed')) return <PortalFeed />;
  return <PortalDashboard />;
}

export default function BusinessPortalLayout() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const myOrg = useMyOrg();
  const activeIncident = useAuthStore(s => s.activeIncident);
  const loadingOrgs = useMainStreetStore(s => s.loadingOrgs);
  const loadOrganizations = useMainStreetStore(s => s.loadOrganizations);
  const loadFacilities = useMainStreetStore(s => s.loadFacilities);
  const loadContacts = useMainStreetStore(s => s.loadContacts);
  const loadStatusChecks = useMainStreetOpsStore(s => s.loadStatusChecks);
  const loadStatusResponses = useMainStreetOpsStore(s => s.loadStatusResponses);
  const loadDamageReports = useMainStreetOpsStore(s => s.loadDamageReports);
  const loadResourceOffers = useMainStreetOpsStore(s => s.loadResourceOffers);
  const loadReentryAuths = useMainStreetOpsStore(s => s.loadReentryAuths);
  const loadReentryPasses = useMainStreetOpsStore(s => s.loadReentryPasses);
  const loadPosts = useMainStreetOpsStore(s => s.loadPosts);

  // Data for badge counts
  const statusChecks = useMainStreetOpsStore(s => s.statusChecks);
  const myResponses = useMyStatusResponses();
  const resourceOffers = useMainStreetOpsStore(s => s.resourceOffers);
  const reentryAuths = useMainStreetOpsStore(s => s.reentryAuths);
  const reentryPasses = useMainStreetOpsStore(s => s.reentryPasses);
  const posts = useMainStreetOpsStore(s => s.posts);

  useEffect(() => {
    loadOrganizations();
    loadFacilities();
    loadContacts();
    loadStatusChecks();
    loadStatusResponses();
    loadDamageReports();
    loadResourceOffers();
    loadReentryAuths();
    loadReentryPasses();
    loadPosts();
  }, []);

  // ── Badge counts ────────────────────────────────────────────

  // Status: pending checks targeting this org that haven't been answered
  const pendingChecks = useMemo(() => {
    if (!myOrg) return 0;
    const orgId = String(myOrg.id);
    const sector = myOrg.sectorType ?? '';
    const respondedCheckIds = new Set(myResponses.map(r => r.statusCheckId));

    return statusChecks.filter(sc => {
      if (sc.status !== 'open') return false;
      if (activeIncident && String(sc.incidentId) !== String(activeIncident.id)) return false;
      if (respondedCheckIds.has(String(sc.id))) return false;
      if (sc.targetType === 'all') return true;
      if (sc.targetType === 'sector' && sc.targetSectors.split(',').includes(sector)) return true;
      if (sc.targetType === 'specific' && sc.targetOrgs.split(',').includes(orgId)) return true;
      return false;
    }).length;
  }, [statusChecks, myOrg, myResponses, activeIncident]);

  // Resources: available offers from OTHER orgs (status = 'offered')
  const availableResources = useMemo(() => {
    if (!myOrg) return 0;
    const orgId = String(myOrg.id);
    return resourceOffers.filter(r => {
      if (r.orgId === orgId) return false; // skip own
      if (r.status !== 'offered') return false;
      if (activeIncident && String(r.incidentId) !== String(activeIncident.id)) return false;
      return true;
    }).length;
  }, [resourceOffers, myOrg, activeIncident]);

  // Re-entry: active passes for this org (by orgId or applicable auth)
  const activePassCount = useMemo(() => {
    if (!myOrg) return 0;
    const orgId = String(myOrg.id);
    const sector = myOrg.sectorType ?? '';

    const applicableAuthIds = new Set<string>();
    for (const auth of reentryAuths) {
      if (auth.targetType === 'all') { applicableAuthIds.add(String(auth.id)); applicableAuthIds.add(String(auth.Rid)); }
      else if (auth.targetType === 'sector' && sector && auth.targetSectors.split(',').includes(sector)) { applicableAuthIds.add(String(auth.id)); applicableAuthIds.add(String(auth.Rid)); }
      else if (auth.targetType === 'specific' && auth.targetOrgs.split(',').includes(orgId)) { applicableAuthIds.add(String(auth.id)); applicableAuthIds.add(String(auth.Rid)); }
    }

    return reentryPasses.filter(p => {
      if (p.status === 'revoked') return false;
      if (p.expiresAt && new Date(p.expiresAt) < new Date()) return false;
      if (p.orgId === orgId) return true;
      if (p.authId && applicableAuthIds.has(p.authId)) return true;
      return false;
    }).length;
  }, [reentryPasses, reentryAuths, myOrg]);

  // Feed: posts in last 24 hours for current incident
  const recentPosts = useMemo(() => {
    const cutoff = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
    return posts.filter(p => {
      if (activeIncident && String(p.incidentId) !== String(activeIncident.id)) return false;
      return p.createdAt > cutoff;
    }).length;
  }, [posts, activeIncident]);

  const badgeCounts: Record<string, number> = {
    status: pendingChecks,
    resources: availableResources,
    reentry: activePassCount,
    feed: recentPosts,
  };

  if (loadingOrgs) {
    return (
      <Center p="xl" style={{ minHeight: 300 }}>
        <Loader size="sm" />
      </Center>
    );
  }

  if (!myOrg && !loadingOrgs) {
    return <PortalOnboarding />;
  }

  return (
    <div style={{ padding: '24px' }}>
      <Group gap="xs" mb="md" style={{ borderBottom: '1px solid var(--frost)', paddingBottom: 12, flexWrap: 'wrap' }}>
        {PORTAL_NAV.map(item => {
          const active = item.exact ? pathname === item.path : pathname.startsWith(item.path);
          const count = item.badgeKey ? badgeCounts[item.badgeKey] ?? 0 : 0;
          return (
            <Button
              key={item.path}
              variant={active ? 'filled' : 'light'}
              color={active ? brand.blurple : 'gray'}
              size="xs"
              leftSection={<item.icon size={14} />}
              rightSection={count > 0 ? (
                <Badge
                  size="xs"
                  variant="filled"
                  color={item.badgeKey === 'status' ? 'red' : 'blue'}
                  circle
                  style={{ minWidth: 18, height: 18, padding: '0 4px' }}
                >
                  {count > 99 ? '99+' : count}
                </Badge>
              ) : undefined}
              onClick={() => navigate(item.path)}
            >
              {item.label}
            </Button>
          );
        })}
      </Group>
      {resolvePortalPage(pathname)}
    </div>
  );
}
